import { AssetGroup } from '@play-co/timestep-core';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import {
  ButtonView,
  animate,
  ImageScaleView,
  View,
} from '@play-co/timestep-core/ui';
import { downscale } from 'src/conf/scale';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { Feature } from 'src/replicant/analytics';
import StateObserver from 'src/StateObserver';
import i18n from 'src/lib/i18n/i18n';
import { waitForItPromise } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import { RadialShine } from 'src/game/components/shared/RadialShine';

type CloseHandler = () => void;

export default class PopupTreasureExpressInfo extends PopupBasicLazy {
  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = downscale(1080);
    const height = downscale(1920);

    super({
      superview: opts.superview,
      skipBackground: true,
      skipTitle: true,
      closeableWithBg: true,
      close: () => opts.close(),
      tracking: {
        feature: Feature.WEEKLY_LEADERBOARD._,
        $subFeature: Feature.WEEKLY_LEADERBOARD.POPUP_INFO,
      },
      zIndex: 10000,
    });

    this.bg.updateOpts({
      backgroundColor: 'rgba(71, 24, 67, 0.9)',
    });

    this.buttonClose.removeFromSuperview();

    // Screen tap area
    new ButtonView({
      superview: this.box,
      width: width,
      height: height,
      zIndex: 1,
      onClick: () => {
        sounds.sfx.play('ok');
        opts.close();
      },
    });
  }

  public async fadeIn() {
    await super.fadeIn();

    //Animate in all items
    await waitForItPromise(300);

    sounds.sfx.play('win');
    const locale = StateObserver.getLocale();
    const isEn = locale === 'en';

    const container = new FlexBoxView({
      superview: this.bg,
      flex: {
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
      },
    });

    // Title

    const title = new ImageScaleView({
      superview: this.box,
      scaleMethod: 'contain',
      canHandleEvents: false,
      centerOnOrigin: true,
      image: `assets/ui/treasureExpress/shared/title_${locale}.png`,
      width: downscale(isEn ? 653 : 600) * 1.2,
      height: downscale(isEn ? 115 : 102) * 1.2,
      x: this.box.style.width * 0.5,
      y: -60,
    });
    await this.showAnimated(title);

    const subtitle = new LangBitmapFontTextView({
      superview: this.box,
      x: this.box.style.width * 0.5,
      y: -10,
      align: 'center',
      verticalAlign: 'center',
      size: 34,
      color: 'white',
      wordWrap: false,
      scale: 0.5,
      font: 'Title',
      localeText: () => i18n('treasureExpress.popupInfo.subtitle'),
    });
    await this.showAnimated(subtitle);

    // Instructions

    const subInfo = new ImageScaleView({
      superview: this.box,
      scaleMethod: 'contain',
      canHandleEvents: false,
      image: `assets/ui/treasureExpress/popupInfo/info_sub_${locale}.png`,
      width: downscale(542),
      height: downscale(isEn ? 359 : 363),
      x: this.box.style.width * 0.5 + 10,
      y: subtitle.style.y + 200,
      centerOnOrigin: true,
    });
    await this.showAnimated(subInfo);

    const ticketsContainer = new FlexBoxView({
      height: 376,
      superview: this.box,
      centerOnOrigin: false,
      anchorX: this.box.style.width * 0.5 + 50,
      x: -20,
      y: subInfo.style.y + 100,
      flex: {
        marginTop: -20,
        justifyContent: 'center',
        alignItems: 'center',
      },
    });

    // Ticket Images

    for (let i = 1; i < 4; i++) {
      new ImageScaleView({
        superview: ticketsContainer,
        scaleMethod: 'contain',
        canHandleEvents: false,
        image: `assets/ui/treasureExpress/popupInfo/${i}_try_${locale}.png`,
        width: downscale(390) * 0.8,
        height: downscale(isEn ? 375 : 376) * 0.8,
      });
    }
    await this.showAnimated(ticketsContainer);

    const arrow = new ImageScaleView({
      superview: this.box,
      scaleMethod: 'contain',
      canHandleEvents: false,
      centerOnOrigin: true,
      image: `assets/ui/treasureExpress/popupInfo/arrow.png`,
      width: downscale(88),
      height: downscale(118),
      x: this.box.style.width * 0.5,
      y: ticketsContainer.style.y + 325,
    });
    await this.showAnimated(arrow);

    const item3 = new View({
      superview: this.box,
      x: this.box.style.width * 0.5,
      y: arrow.style.y + 200,
    });

    new RadialShine({
      superview: item3,
      canHandleEvents: false,
      centerOnOrigin: true,
      scale: 1.8,
    });

    new ImageScaleView({
      superview: item3,
      scaleMethod: 'contain',
      scale: 0.8,
      canHandleEvents: false,
      centerOnOrigin: true,
      image: `assets/ui/treasureExpress/popupInfo/claim_rewards_${locale}.png`,
      width: downscale(705),
      height: downscale(isEn ? 352 : 353),
    });

    await this.showAnimated(item3);

    // CTA
    const tapToContinue = new LangBitmapFontTextView({
      superview: this.box,
      centerOnOrigin: true,
      x: this.box.style.width * 0.5,
      y: item3.style.y + 200,
      height: 200,
      align: 'center',
      verticalAlign: 'center',
      size: 40,
      color: '#ffe380',
      wordWrap: false,
      font: 'Title',
      localeText: () => i18n('treasureExpress.popupInfo.tapToContinue'),
    });
    await this.showAnimated(tapToContinue);
  }

  protected getAssetsGroup(): AssetGroup | null {
    return new AssetGroup([
      ...AssetGroup.constructURLs(['assets/ui/treasureExpress/shared']),
      ...AssetGroup.constructURLs(['assets/ui/treasureExpress/popupInfo']),
    ]);
  }

  private async showAnimated(view: View) {
    view.updateOpts({ scale: 0 });
    animate(view).then({ scale: 1 }, 300, animate.easeOutBack);
    sounds.sfx.play('puzzle-ice-tap');
    await waitForItPromise(200);
  }
}
